import { SearchIcon } from "@chakra-ui/icons";
import { Button, InputGroup, InputRightElement, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import React from "react";
import DataTable from "../../../shared/components/DataTable/DataTable";
import useQueryDataTable from "../../../shared/components/DataTable/useQueryDataTable";
import DebouncedInput from "../../../shared/components/DebouncedInput";
import Select from "../../../shared/components/Select";
import useApi from "../../../shared/hooks/useApi";
import {
  ALLOWED_SHOW_ON_AGENCY_TYPES,
  ALLOWED_VBP_TYPES,
  ANSWER_TYPE_LABELS,
  NursingQuestion,
  NursingQuestionAnswerType,
} from "../nursing-question-settings.consts";
import { filterNursingQuestions } from "../nursing-question-settings.utils";
import NursingQuestionModal, { NursingQuestionModalState } from "./NursingQuestionModal";
import {
  PossibleAnswersCell,
  QuestionActionsCell,
  QuestionTextCell,
  QuestionToggleCell,
} from "./NursingQuestionCells";

const YES_NO_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const ANSWER_TYPE_OPTIONS = Object.entries(ANSWER_TYPE_LABELS).map(([value, label]) => ({
  label,
  value: value as NursingQuestionAnswerType,
}));

export default function NursingQuestionsTable() {
  const { queries } = useApi();

  const [searchTerm, setSearchTerm] = React.useState("");
  const [answerTypes, setAnswerTypes] = React.useState<NursingQuestionAnswerType[]>([]);
  const [isVBP, setIsVBP] = React.useState<boolean | null>(null);
  const [isVital, setIsVital] = React.useState<boolean | null>(null);
  const [showOnPatient, setShowOnPatient] = React.useState<boolean | null>(null);
  const [showOnAgency, setShowOnAgency] = React.useState<boolean | null>(null);
  const [modalState, setModalState] = React.useState<NursingQuestionModalState>({
    type: "closed",
  });

  const query = useQuery({
    ...queries.nursingQuestionSettings.list(),
    select: (data) =>
      filterNursingQuestions(data.questions, {
        answerTypes,
        isVBP,
        isVital,
        searchTerm,
        showOnAgency,
        showOnPatient,
      }),
  });

  const openEditAlerts = React.useCallback(
    (question: NursingQuestion) => setModalState({ question, type: "editAlerts" }),
    []
  );

  const columns = React.useMemo(() => createColumns({ openEditAlerts }), [openEditAlerts]);

  const { dataTableProps } = useQueryDataTable({
    columns,
    columnVisiblity: { storage: { key: "nursing-question-settings", version: 1 } },
    query,
  });

  return (
    <>
      <DataTable
        {...dataTableProps}
        actionNode={
          <Button colorScheme="blue" onClick={() => setModalState({ type: "new" })}>
            New Question
          </Button>
        }
        filterNode={
          <>
            <InputGroup width="xs">
              <DebouncedInput
                debounce={250}
                placeholder="Search by question name or answer"
                value={searchTerm}
                onChange={setSearchTerm}
              />
              <InputRightElement>
                <SearchIcon color="gray.400" />
              </InputRightElement>
            </InputGroup>
            <Select
              allowUnselect={true}
              label="Question Type"
              multiple={true}
              options={ANSWER_TYPE_OPTIONS}
              value={answerTypes.length === 0 ? null : answerTypes}
              onChange={(value) => setAnswerTypes(value ?? [])}
            />
            <Select
              allowUnselect={true}
              label="Is for VBP"
              multiple={false}
              options={YES_NO_OPTIONS}
              value={isVBP}
              onChange={(value) => setIsVBP(value ?? null)}
            />
            <Select
              allowUnselect={true}
              label="Is Vital"
              multiple={false}
              options={YES_NO_OPTIONS}
              value={isVital}
              onChange={(value) => setIsVital(value ?? null)}
            />
            <Select
              allowUnselect={true}
              label="Show on Patient"
              multiple={false}
              options={YES_NO_OPTIONS}
              value={showOnPatient}
              onChange={(value) => setShowOnPatient(value ?? null)}
            />
            <Select
              allowUnselect={true}
              label="Show on Agency"
              multiple={false}
              options={YES_NO_OPTIONS}
              value={showOnAgency}
              onChange={(value) => setShowOnAgency(value ?? null)}
            />
          </>
        }
      />
      <NursingQuestionModal
        modalState={modalState}
        onClose={() => setModalState({ type: "closed" })}
      />
    </>
  );
}

function createColumns(params: { openEditAlerts: (question: NursingQuestion) => void }) {
  const columnHelper = createColumnHelper<NursingQuestion>();

  return [
    columnHelper.accessor("questionText", {
      cell: (info) => <QuestionTextCell question={info.row.original} />,
      header: "Text",
      sortingFn: "text",
    }),
    columnHelper.accessor("answerType", {
      cell: (info) => <Text>{ANSWER_TYPE_LABELS[info.getValue()]}</Text>,
      header: "Question Type",
      sortingFn: "text",
    }),
    columnHelper.accessor("possibleAnswers", {
      cell: (info) => <PossibleAnswersCell question={info.row.original} />,
      enableSorting: false,
      header: "Possible Answers",
      meta: {
        csvFn(answers: NursingQuestion["possibleAnswers"]) {
          return (answers ?? []).map((answer) => answer.text).join(", ");
        },
      },
    }),
    columnHelper.accessor("isVBP", {
      cell: (info) => (
        <QuestionToggleCell
          field="isVBP"
          isDisabled={!ALLOWED_VBP_TYPES.includes(info.row.original.answerType)}
          question={info.row.original}
        />
      ),
      header: "Is VBP",
    }),
    columnHelper.accessor("showOnPatient", {
      cell: (info) => (
        <QuestionToggleCell field="showOnPatient" isDisabled={false} question={info.row.original} />
      ),
      header: "Show on Patient",
    }),
    columnHelper.accessor("showOnAgency", {
      cell: (info) => (
        <QuestionToggleCell
          field="showOnAgency"
          isDisabled={!ALLOWED_SHOW_ON_AGENCY_TYPES.includes(info.row.original.answerType)}
          question={info.row.original}
        />
      ),
      header: "Show on Agency",
    }),
    columnHelper.accessor("transferAnswerBetweenTasks", {
      cell: (info) => (
        <QuestionToggleCell
          field="transferAnswerBetweenTasks"
          isDisabled={false}
          question={info.row.original}
        />
      ),
      header: "Transfer Between Tasks",
    }),
    columnHelper.display({
      cell: (info) => (
        <QuestionActionsCell question={info.row.original} onEditAlerts={params.openEditAlerts} />
      ),
      header: "",
      id: "_actions",
    }),
  ];
}
